import { useEffect, useState } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { Button } from '@pepper/ui'

import ModalWrapper, {
  ModalBody,
  showModalWrapper,
} from 'components/ModalWrapper'
import AddDomain from './AddDomain'
import IntegrateDomain from './IntegrateDomain'

import s from './AddDomainModal.module.css'

type FormValues = {
  mainDomain: string
  competitorUrl?: string[]
  country: SEOCountries
  brandWords: string[]
}

type AddDomainProps = {
  refetch: () => void
}

const sideNavDetails = [
  {
    header: 'Add',
    description: 'Enter website domain that you wish to track on Pepper CMP.',
  },
  {
    header: 'Connect',
    description:
      'Integrate your Google account for better and smarter insights.',
  },
]

const HelperBox = () => {
  return (
    <div className={s.infoBox}>
      <div className={s.icon}>
        <Icon icon="lucide:lightbulb" width={16} />
      </div>
      <div className={s.infoDescHeader}>What is a Domain</div>
      <div className={s.infoDescText}>
        A domain is like a tailored profile that guides your keyword and topic
        creation, content strategies, and more.
      </div>
      <div className={s.infoDescHelp}>
        <Button
          type="link"
          className={s.HelpArticleBtn}
          href="https://pepperc.notion.site/How-to-set-up-a-new-domain-on-Pepper-c5a7578b05dd48768d4a9d71b1d7a1a5?pvs=4"
          target="_blank"
        >
          <Icon icon="lucide:help-circle" width={14} />
          Help Article
        </Button>
      </div>
    </div>
  )
}

const AddDomainModal = NiceModal.create(({ refetch }: AddDomainProps) => {
  const initialValues: FormValues = {
    country: 'us',
    mainDomain: '',
    competitorUrl: ['', ''],
    brandWords: [],
  }
  const [step1FormValues, setStep1FormValues] =
    useState<FormValues>(initialValues)

  const { visible: isModalVisible, remove: removeSelfModal } =
    useModal('add-domain')

  const [currentStepSelected, setCurrentStepSelected] = useState(0)

  const closeModal = () => {
    removeSelfModal()
    refetch()
  }

  const getProcessList = () => {
    return sideNavDetails.map((sideNav, index) => {
      return (
        <div
          className={clsx(s.sideNavList, {
            [s.activeNav]: index === currentStepSelected,
            [s.passedNav]: index < currentStepSelected,
          })}
          key={sideNav.header}
        >
          <li className={s.navItem}>
            <span className={s.navIndex}>
              {index < currentStepSelected ? (
                <Icon icon="lucide:check" width="16" className={s.navChecked} />
              ) : (
                index + 1
              )}
            </span>
            <span className={s.navText}>{sideNav.header}</span>
            {index === currentStepSelected && (
              <Icon
                icon="lucide:chevron-right"
                width="16"
                height="16"
                className={s.navArrow}
              />
            )}
          </li>
          {sideNav.description && index === currentStepSelected && (
            <div className={s.navDesc}>{sideNav.description}</div>
          )}
        </div>
      )
    })
  }

  useEffect(() => {
    if (isModalVisible) showModalWrapper()
  }, [isModalVisible])

  const handleNext = (domainFormValues: FormValues) => {
    setStep1FormValues(domainFormValues)
    setCurrentStepSelected(currentStepSelected + 1)
  }

  const handlePrevious = () => {
    setCurrentStepSelected(currentStepSelected - 1)
  }

  return (
    <ModalWrapper
      modalId="add-domain"
      id="seo-headless-modal"
      onCancel={removeSelfModal}
      className={s.domainModalWrapper}
      destroyOnClose={true}
    >
      <ModalBody>
        <div className={s.addDomainWrap}>
          <div className={s.createDomainNavWrap}>
            <ul className={s.processNav}>{getProcessList()}</ul>
            {currentStepSelected === 0 && <HelperBox />}
          </div>
          <div className={s.createDomainArea}>
            {currentStepSelected === 0 && (
              <AddDomain onSubmit={handleNext} formValues={step1FormValues} />
            )}
            {currentStepSelected === 1 && (
              <IntegrateDomain
                onPrevious={handlePrevious}
                removeSelfModal={closeModal}
                formValues={step1FormValues}
              />
            )}
          </div>
        </div>
      </ModalBody>
    </ModalWrapper>
  )
})
export default AddDomainModal
